/*
 * Certificate area (audit Phase 3.3). Replaces wwwroot/js/Certificate/List.js,
 * Detail.js and Import.js on the Phase 3.1 core. No jQuery.
 *
 * Three screens, one modal. The list (Certificate/Domain/{id}) and the
 * expiring-soon list (Certificate/Expiring/{id}) both render CertificateRow and
 * both carry #CertificateModal, into which Detail and Import are loaded.
 *
 * Which list to redraw after a write is the modal placeholder's job to say:
 * `#CertificateModal[data-list-url]`. The legacy code rebuilt the URL from
 * #CurrentDomainId and gave up when it was 0 -- which is exactly the (Global)
 * expiring scope, so on that screen a delete reported success and left the row
 * on the page. See listUrl().
 *
 * The Import modal's initialisation is an ENHANCER, not a call the handler that
 * opened the dialog has to remember: the server re-renders this same partial
 * after a refusal, and the legacy Submit had to re-run Init() by hand on that
 * one path. The radio and file controls carry data-action rather than their own
 * listeners, so nothing has to be re-bound at all.
 */
import { ajaxJson, ajaxModal, ajaxSwap, failToast, summaryOf } from '../core/ajax';
import { all, closest, one, swapHtml } from '../core/dom';
import { request, xhrFromError } from '../core/http';
import { confirm, hideModalThen } from '../core/modal';
import { toast } from '../core/toast';

const MODAL = 'CertificateModal';
const IMPORT_FORM = 'CertificateImportForm';

/** The domain the rail is pointed at. Only the quick-bind needs it. */
function currentDomainId(): number {
    const raw = (one('#CurrentDomainId') as HTMLInputElement | null)?.value ?? '';
    const parsed = Number.parseInt(raw, 10);
    return Number.isFinite(parsed) && parsed > 0 ? parsed : 0;
}

/**
 * Where the list behind the modal came from.
 *
 * Stamped by whichever list view rendered the placeholder, so a write made in
 * the Detail modal redraws the screen the operator is actually on -- the
 * domain list or the expiring list, at whatever scope it was opened. Falling
 * back to the rail's domain keeps a partial rendered by something else working;
 * returning null (no list on the page) means there is nothing to redraw, which
 * is not a failure.
 */
export function listUrl(): string | null {
    const stamped = (one(`#${MODAL}`) as HTMLElement | null)?.getAttribute('data-list-url');
    if (stamped) return stamped;
    const domainId = currentDomainId();
    return domainId > 0 ? `Certificate/Domain/${domainId}` : null;
}

/** Re-fetch the list the modal was opened from. */
function reloadList(): Promise<void> {
    const url = listUrl();
    if (!url) return Promise.resolve();
    return ajaxSwap({ container: 'ViewContainer', url, alertTitle: 'Failed to refresh the certificate list' });
}

/**
 * Close the dialog, THEN redraw. The list partial contains the modal
 * placeholder, so swapping the view container first tears the dialog out of the
 * document mid-transition and hidden.bs.modal fires on a detached node.
 */
function closeThenReload(): void {
    hideModalThen(MODAL, () => void reloadList());
}

export const List = {
    ShowDetail(certificateId: number | string): void {
        void ajaxModal({
            modal: MODAL,
            url: `Certificate/${certificateId}`,
            title: null,
            errorTitle: `Certificate ${certificateId}`,
            errorMessage: 'Certificate Detail failed to execute:',
        });
    },

    Import(): void {
        // No `title:` -- Import.cshtml owns the whole modal-dialog, header and
        // footer included; a title would wrap a second dialog around it.
        void ajaxModal({
            modal: MODAL,
            url: 'Certificate/Import',
            title: null,
            errorTitle: 'Import Certificate',
            errorMessage: 'Import Certificate view failed to execute:',
        });
    },

    Delete(certificateId: number | string): void {
        void confirm({
            title: 'Delete Certificate',
            html: 'Delete this certificate?<br/><br/>'
                + '<span class="text-danger">Signatures already made with it stop being verifiable. '
                + 'Retire it instead to keep historical chains intact.</span>',
            confirmText: 'Delete',
            variant: 'danger',
            onConfirm: () => {
                void ajaxJson({
                    url: `Certificate/${certificateId}`,
                    method: 'DELETE',
                    errorTitle: 'Failed to delete certificate',
                    onSuccess: () => {
                        toast('success', 'Certificate deleted');
                        void reloadList();
                    },
                    onFailure: data => {
                        toast('danger', 'Failed to delete certificate',
                            summaryOf(data) || 'The server gave no reason. Check the Api log for the failed delete.');
                        return true;
                    },
                    onError: (xhr, _status, error) => {
                        toast('danger', 'Failed to delete certificate', `HTTP ${xhr.status} — ${error}`);
                        return true;
                    },
                });
            },
        });
    },

    /**
     * Quick-bind for the Unbound section: bind to whichever domain the rail is
     * pointed at. Multi-domain binding is the Detail modal's Domains tab.
     */
    BindToCurrent(certificateId: number | string): void {
        const domainId = currentDomainId();
        if (!domainId) {
            toast('danger', 'Pick a domain first', 'The rail\'s domain picker decides what this binds to.');
            return;
        }
        void ajaxJson({
            url: `Certificate/${certificateId}/Domain/${domainId}`,
            method: 'POST',
            errorTitle: 'Failed to bind certificate',
            onSuccess: () => {
                toast('success', 'Certificate bound to the current domain');
                void reloadList();
            },
            onFailure: failToast('Failed to bind certificate'),
        });
    },
};

export const Detail = {
    Delete(certificateId: number | string): void {
        void confirm({
            title: 'Delete Certificate',
            html: 'Delete this certificate?<br/><br/>'
                + '<span class="text-danger">Signatures already made with it stop being verifiable. '
                + 'Retire it instead to keep historical chains intact.</span>',
            confirmText: 'Delete',
            variant: 'danger',
            onConfirm: () => {
                void ajaxJson({
                    url: `Certificate/${certificateId}`,
                    method: 'DELETE',
                    errorTitle: 'Failed to delete certificate',
                    onSuccess: () => {
                        toast('success', 'Certificate deleted');
                        closeThenReload();
                    },
                    onFailure: failToast('Failed to delete certificate'),
                });
            },
        });
    },

    /**
     * Retire = soft-disable: hidden from new bindings and refused at
     * license-sign time, but the row stays so historical signatures remain
     * verifiable. The Api refuses a cert that is still actively bound and names
     * the references to unpick, which is what the failure toast carries.
     *
     * The reason is collected in the confirm dialog's html slot. The dialog is
     * hidden by the time onConfirm runs, but its body is not cleared until the
     * next confirm() call, so the input is still readable.
     */
    Retire(certificateId: number | string): void {
        void confirm({
            title: 'Retire Certificate',
            html: '<p>Retire this certificate? It is hidden from new bindings and refused at license-sign time. '
                + 'The row stays, so historical signatures remain verifiable.</p>'
                + '<label for="CertificateRetireReason" class="form-label">Reason (optional)</label>'
                + '<input type="text" id="CertificateRetireReason" class="form-control" maxlength="200" '
                + 'placeholder="e.g. expiring soon, replaced by cert #N" />',
            confirmText: 'Retire',
            variant: 'warning',
            onConfirm: () => {
                const reason = (one('#CertificateRetireReason') as HTMLInputElement | null)?.value ?? '';
                void ajaxJson({
                    url: `Certificate/${certificateId}/Retire`,
                    method: 'POST',
                    data: { reason },
                    errorTitle: 'Failed to retire certificate',
                    onSuccess: () => {
                        toast('success', 'Certificate retired');
                        closeThenReload();
                    },
                    onFailure: failToast('Failed to retire certificate'),
                });
            },
        });
    },

    /**
     * Move every active SAML, mTLS and license-signing binding from this
     * certificate to its designated successor and retire this one. Atomic on
     * the server. The scheduled date is advisory, so the button is live before
     * it -- only its wording and weight change ("now" vs "early").
     */
    ExecuteCutover(predecessorId: number | string, successorId: number | string): void {
        void confirm({
            title: 'Execute Rotation Cutover',
            html: `<p>Move all active SAML, mTLS and license-signing bindings from <strong>certificate #${predecessorId}</strong> `
                + `to <strong>certificate #${successorId}</strong>?</p>`
                + `<p>This certificate (#${predecessorId}) is retired immediately with reason `
                + `"Rotated to cert #${successorId}". Operations against it fail until you re-point them; `
                + 'the new bindings on the successor take effect at once.</p>',
            confirmText: 'Execute Cutover',
            variant: 'warning',
            onConfirm: () => {
                void ajaxJson({
                    url: `Certificate/${predecessorId}/ExecuteCutover`,
                    method: 'POST',
                    errorTitle: 'Cutover failed',
                    onSuccess: () => {
                        toast('success', 'Rotation cutover executed', `Cert #${successorId} now carries the bindings.`);
                        closeThenReload();
                    },
                    onFailure: failToast('Cutover failed'),
                });
            },
        });
    },

    /**
     * Domains tab: every checkbox flip is its own bind/unbind, so each binding
     * change is independently auditable and client and server cannot drift out
     * of step behind a batch Save. On refusal the box goes back to where it was
     * -- the checkbox has to keep telling the truth about the server.
     *
     * `this` is the checkbox: core/dispatch fires a data-action on CHANGE for a
     * checkbox, not on click.
     */
    ToggleDomain(this: Element): void {
        const checkbox = this as HTMLInputElement;
        const row = closest(checkbox, '.list-group-item');
        const busy = row?.querySelector('.cert-domain-busy');
        const certificateId = (one('#CertificateDomainsList') as HTMLElement | null)?.getAttribute('data-certificate-id');
        const domainId = checkbox.getAttribute('data-domain-id');
        if (!certificateId || !domainId) return;

        const bound = checkbox.checked;
        const label = bound ? 'bind domain' : 'unbind domain';
        const revert = (): void => { checkbox.checked = !bound; };
        checkbox.disabled = true;
        void ajaxJson({
            url: `Certificate/${certificateId}/Domain/${domainId}`,
            method: bound ? 'POST' : 'DELETE',
            busy,
            errorTitle: `Failed to ${label}`,
            onSuccess: () => toast('success', bound ? 'Bound to domain' : 'Unbound from domain'),
            onFailure: data => {
                revert();
                toast('danger', `Failed to ${label}`, summaryOf(data) || 'The server rejected the change.');
                return true;
            },
            onError: (xhr, _status, error) => {
                revert();
                toast('danger', `Failed to ${label}`, `HTTP ${xhr.status} — ${error}`);
                return true;
            },
            onComplete: () => { checkbox.disabled = false; },
        });
    },
};

// ---------------------------------------------------------------- import

/** Paste-as-text or upload-a-file; the inactive half is disabled, not just dimmed. */
function applyImportMode(root: ParentNode, mode: string): void {
    const fileMode = mode === 'file';
    const disable = (selector: string, disabled: boolean): void => {
        const node = root.querySelector(selector);
        if (node instanceof HTMLInputElement || node instanceof HTMLTextAreaElement) node.disabled = disabled;
    };
    disable('#CertificateBody', fileMode);
    disable('#CertificateFileSelector', !fileMode);
    disable('#PrivateKeyPassword', !fileMode);
    // The Select File control is a <label for>, which keeps working when the
    // input behind it is disabled; it has to be taken out of the flow itself.
    const picker = root.querySelector('label[for=CertificateFileSelector]');
    if (picker instanceof HTMLElement) {
        picker.classList.toggle('disabled', !fileMode);
        picker.style.pointerEvents = fileMode ? '' : 'none';
    }
    for (const section of Array.from(root.querySelectorAll('.import-section-text'))) {
        section.classList.toggle('cert-section-inactive', fileMode);
    }
    for (const section of Array.from(root.querySelectorAll('.import-section-file'))) {
        section.classList.toggle('cert-section-inactive', !fileMode);
    }
}

/**
 * Fill the "Rotates from" picker with the non-retired certs of a domain.
 *
 * The dedicated RotationCandidates route, never Certificate/Domain/{id}: that
 * one returns the list PARTIAL, so asking it for JSON parse-failed in silence
 * and this dropdown was empty on every import the console has ever done.
 * Failure here is non-fatal -- a standalone import still works.
 */
async function loadRotationCandidates(select: HTMLSelectElement, domainId: number): Promise<void> {
    if (!domainId) return;
    try {
        const result = await request(`Certificate/RotationCandidates/${domainId}`, { accept: 'json' });
        const candidates = result.json;
        if (!Array.isArray(candidates)) return;
        for (const candidate of candidates) {
            const row = candidate as { id?: unknown; label?: unknown };
            const option = document.createElement('option');
            option.value = String(row.id ?? '');
            option.textContent = String(row.label ?? '');
            select.appendChild(option);
        }
    } catch {
        // Non-fatal by design.
    }
}

/**
 * Initialise the Import form wherever it lands -- the first open, and the
 * re-render the server sends back after a refusal (an unreadable body, an unfit
 * purpose, the validity confirmation). The legacy Submit had to call Init()
 * again by hand on that second path, which is the kind of thing an enhancer
 * exists to stop being anyone's job.
 */
export function enhanceCertificateImport(root: ParentNode): void {
    const forms: Element[] = [];
    if (root instanceof Element && root.id === IMPORT_FORM) forms.push(root);
    forms.push(...Array.from(root.querySelectorAll(`#${IMPORT_FORM}`)));
    for (const form of forms) {
        if (form.getAttribute('data-mcp-enhanced') === 'true') continue;
        form.setAttribute('data-mcp-enhanced', 'true');

        // The cert has to land in a domain's list to be findable, and only the
        // rail knows which domain the operator is standing in. 0 = no binding;
        // the list surfaces those in its Unbound section.
        const domainId = currentDomainId();
        const domainField = form.querySelector('#CertificateImportDomainId');
        if (domainField instanceof HTMLInputElement) domainField.value = String(domainId);

        const checked = form.querySelector('input[name=ImportType]:checked');
        applyImportMode(form, (checked as HTMLInputElement | null)?.value ?? 'text');

        const rotatesFrom = form.querySelector('#CertificateRotatesFrom');
        if (rotatesFrom instanceof HTMLSelectElement) void loadRotationCandidates(rotatesFrom, domainId);
    }
}

export const Import = {
    /** The source radios. `this` is the radio; core/dispatch fires it on change. */
    ModeChanged(this: Element): void {
        const form = closest(this, `#${IMPORT_FORM}`);
        if (form) applyImportMode(form, (this as HTMLInputElement).value);
    },

    /**
     * Show the chosen filename. Never `input.value`, which is
     * "C:\fakepath\<name>" under the file-input security model.
     */
    FileChosen(this: Element): void {
        const input = this as HTMLInputElement;
        const label = one('#SelectedCertificateFile');
        if (!label) return;
        const chosen = input.files?.[0]?.name;
        label.textContent = chosen ?? 'No file selected';
        label.classList.toggle('fst-italic', !chosen);
        label.classList.toggle('text-body-secondary', !chosen);
    },

    Submit(): void {
        const form = one(`#${IMPORT_FORM}`) as HTMLFormElement | null;
        if (!form) return;
        const fileMode = (form.querySelector('input[name=ImportType]:checked') as HTMLInputElement | null)?.value === 'file';

        let purpose = 0;
        for (const flag of all('.cert-purpose-flag')) {
            if ((flag as HTMLInputElement).checked) purpose |= Number.parseInt((flag as HTMLInputElement).value, 10) || 0;
        }
        if (purpose === 0) {
            // The Api refuses CertificatePurpose.None, and the refusal costs a
            // re-render that loses an uploaded file. Say it here instead.
            toast('danger', 'Pick at least one purpose', 'A certificate with no role assigned cannot be stored.');
            return;
        }

        const body = new FormData();
        body.append('Purpose', String(purpose));
        body.append('domainId', (form.querySelector('#CertificateImportDomainId') as HTMLInputElement | null)?.value || '0');

        const rotatesFrom = (form.querySelector('#CertificateRotatesFrom') as HTMLSelectElement | null)?.value;
        if (rotatesFrom) body.append('rotatesFromCertificateId', rotatesFrom);
        const cutover = (form.querySelector('#CertificateScheduledCutover') as HTMLInputElement | null)?.value;
        if (cutover) body.append('scheduledCutoverDate', cutover);
        // Only rendered once the server has refused an expired / not-yet-valid
        // certificate and said what is being confirmed.
        if ((form.querySelector('#CertificateAcceptValidityWarning') as HTMLInputElement | null)?.checked) {
            body.append('acceptValidityWarning', 'true');
        }

        let url = 'Certificate/Import';
        if (fileMode) {
            url = 'Certificate/ImportFile';
            const picker = form.querySelector('#CertificateFileSelector') as HTMLInputElement | null;
            const file = picker?.files?.[0];
            if (!file) {
                toast('danger', 'Choose a certificate file before importing.');
                return;
            }
            // No MIME filter: browsers report PFX as application/x-pkcs12, PEM
            // as application/x-x509-ca-cert, and often as the empty string. The
            // server validates the bytes.
            body.append('file', file);
            body.append('privateKeyPassword', (form.querySelector('#PrivateKeyPassword') as HTMLInputElement | null)?.value ?? '');
        } else {
            const text = (form.querySelector('#CertificateBody') as HTMLTextAreaElement | null)?.value ?? '';
            if (!text.trim()) {
                toast('danger', 'Paste the certificate body before importing.');
                return;
            }
            body.append('CertificateBody', text);
        }

        void submitImport(url, body);
    },
};

/**
 * Post the import and branch on what came back. Success is JSON
 * ({ success, id, notice }); a refusal is the Import partial again, carrying
 * its ModelState and -- after a validity problem -- the confirmation checkbox.
 * Branching on Content-Type rather than on the body's shape is what keeps a
 * serialized envelope from being rendered into the dialog as markup.
 */
async function submitImport(url: string, body: FormData): Promise<void> {
    try {
        const result = await request(url, { method: 'POST', data: body });
        const contentType = result.response.headers.get('Content-Type') ?? '';
        if (contentType.toLowerCase().includes('application/json')) {
            const envelope = result.json as { success?: boolean; notice?: string } | undefined;
            if (envelope?.success) {
                toast('success', 'Certificate imported', envelope.notice ?? '');
                closeThenReload();
                return;
            }
        }
        swapHtml(`#${MODAL}`, result.text, 'replace');
    } catch (error) {
        const xhr = xhrFromError(error);
        toast('danger', 'Failed to import certificate', `HTTP ${xhr.status} — ${xhr.statusText || 'Network error'}`);
    }
}
